import { $ZodIssue } from 'zod/v4/core/index'
import * as z from 'zod'
import { FormInputType, getZodTypes } from '../libs/forms/FormSchema.ts'
import tippy, { Instance } from 'tippy.js'
import 'tippy.js/dist/tippy.css'
import 'tippy.js/animations/scale.css'
import handleCaptcha from './PluginRecaptcha.ts'

/* CAVEATS:
- Each form has to have an unique id (data-form), unless they have exactly the same inputs
- Each input has to have an unique id in the context of the form (data-form-input-id)
- Lackluster generateSchemaFromHTML configuration (missing dynamic rules)
- Currently, all forms use a globally-scoped config - can be modified if needed
- Radio buttons require one of the inputs to be checked from the beginning
*/

enum EventType {
  SUBMIT = 'SUBMIT',
  CHANGE = 'CHANGE',
}

interface IConfig {
  formSelector: string
  inputSelector: string
  inputLabelSelector: string
  inputIdSelector: string
  submitSelector: string
  errorClass: string
  errorType: 'tooltip' | 'text'
  errorShow: 'all' | 'first'
  captchaAttr: boolean
  errorSvg: string
  observeForms: boolean
}

type TSchemasKey = keyof typeof schemas

const ERROR_SVG = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="#ffffff" class="w-6 h-6">
<path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
</svg>`

const DEFAULT_CONFIG: IConfig = {
  formSelector: 'data-form', // must be a form html element
  inputSelector: 'data-form-input', // container element for input
  inputLabelSelector: 'data-form-input-label', // label for input element
  inputIdSelector: 'data-form-input-id', // unique id for input element
  submitSelector: 'data-form-submit', // button element with type="submit"
  errorClass: 'form-error', // error element for displaying inline errors (text)
  errorType: 'tooltip', // error type - tooltip or text
  errorShow: 'first', // show all or only the next error
  captchaAttr: false, // set to true for using captcha for specific forms only
  errorSvg: ERROR_SVG, // tooltip error SVG
  observeForms: false, // set to true to handle dynamic forms (forms added after page load)
}

const globalConfig: IConfig = { ...DEFAULT_CONFIG }

const schemas: Record<string, z.ZodObject> = {}
const tippyInstances = new WeakMap<Element, Instance>()

// Event handlers

const handleSubmit = (form: HTMLFormElement): void => {
  form.onsubmit = (e: Event) => {
    e.preventDefault()

    const formName = form.getAttribute(globalConfig.formSelector)

    if (!formName) return

    const inputContainers = form.querySelectorAll(
      `[${globalConfig.inputSelector}]`,
    )

    const inputs = getInputs(inputContainers)
    const formData = prepareFormData(inputs)

    clearErrors(inputContainers)

    const result = validateForm(formName, formData)

    if (result === true) {
      if (globalConfig.captchaAttr) {
        if (!form.getAttribute('data-captcha')) return form.submit()
        handleCaptcha(form)
      } else {
        handleCaptcha(form)
      }
    } else {
      handleErrors(inputContainers, result, EventType.SUBMIT)
    }
  }
}

const handleChange = (form: HTMLFormElement): void => {
  const formName = form.getAttribute(globalConfig.formSelector)

  if (!formName) return

  const inputContainers = form.querySelectorAll(
    `[${globalConfig.inputSelector}]`,
  )

  const inputs = getInputs(inputContainers)

  inputs.forEach((inputObj) => {
    const key = Object.keys(inputObj)[0]
    const input = inputObj[key]

    if (!input) return

    input.addEventListener('change', () => {
      const formData = prepareFormData(getInputs(inputContainers))

      const result = validateForm(formName, formData)

      if (result === true) {
        if (!input.parentElement) return

        setError(input.parentElement, '')
        return
      }

      const inputContainer = form.querySelectorAll(
        `[${globalConfig.inputSelector}=${key}]`,
      )

      handleErrors(inputContainer, result, EventType.CHANGE)
    })
  })
}

const getInputs = (
  inputContainers: NodeListOf<Element>,
): Record<string, HTMLInputElement>[] => {
  return Array.from(inputContainers).reduce((inputs, inputContainer) => {
    const key = inputContainer.getAttribute(globalConfig.inputIdSelector)
    const input = inputContainer.querySelector(
      'input, textarea, select',
    ) as HTMLInputElement | null

    if (input && key) {
      transformInput(input)
      inputs.push({ [key]: input })
    }

    return inputs
  }, [] as Record<string, HTMLInputElement>[])
}

const prepareFormData = (
  inputs: Record<string, HTMLInputElement>[],
): Record<string, unknown> => {
  return inputs.reduce((formData, input) => {
    const key = Object.keys(input)[0]
    const value = input[key]

    if (value.type === 'checkbox') {
      formData[key] = value.checked.toString()
    } else if (value.type === 'file') {
      formData[key] = value.files?.[0]
    } else {
      formData[key] = value.value
    }

    return formData
  }, {} as Record<string, unknown>)
}

const validateForm = (
  formName: string,
  formData: Record<string, unknown>,
): true | $ZodIssue[] => {
  const schema = schemas?.[formName as TSchemasKey]

  if (!schema) return []

  const result = schema.safeParse(formData)
  return result.success ? true : result.error.issues
}

// Error handling

const handleErrors = (
  inputContainers: NodeListOf<Element>,
  result: $ZodIssue[],
  eventType: EventType,
): void => {
  inputContainers.forEach((inputContainer) => {
    const inputId = inputContainer.getAttribute(
      `${globalConfig.inputIdSelector}`,
    )

    const error = result.find((r) => r.path[0] === inputId)

    // Clear error
    if (!error) {
      setError(inputContainer, '')
      return
    }

    const errorIndex = result.indexOf(error)

    if (eventType === EventType.SUBMIT && errorIndex === 0) {
      inputContainer.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      })
    }

    if (globalConfig.errorShow === 'first' && errorIndex === 0) {
      return setError(inputContainer, result[errorIndex].message)
    }

    if (globalConfig.errorShow === 'all') {
      return setError(inputContainer, result[errorIndex].message)
    }
  })
}

const setError = (inputContainer: Element, message: string): void => {
  if (globalConfig.errorType === 'tooltip') {
    // Get existing instance
    let instance = tippyInstances.get(inputContainer)

    if (instance) {
      // Update existing instance
      instance.setContent(
        `<div class="tooltip-error">${globalConfig.errorSvg} ${message}</div>`,
      )

      if (!message) return instance.hide()

      instance.show()
    } else if (message) {
      // Create new instance only if doesn't exist
      instance = tippy(inputContainer, {
        content: `<div class="tooltip-error">${globalConfig.errorSvg} ${message}</div>`,
        allowHTML: true,
        trigger: 'manual',
        theme: 'material',
        animation: 'scale',
        placement: 'bottom',
        offset: [0, 10],
      })

      instance.show()
      tippyInstances.set(inputContainer, instance)
    }
  }

  if (globalConfig.errorType === 'text') {
    let elError = inputContainer.querySelector(
      `.${globalConfig.errorClass}`,
    ) as HTMLElement | null

    if (!elError) {
      const input = inputContainer.querySelector(
        'input, textarea, select',
      ) as HTMLInputElement | null

      elError = document.createElement('div')
      elError.classList.add(globalConfig.errorClass)
      elError.id = `${input?.name}-error`
      inputContainer.appendChild(elError)
    }

    elError.innerHTML = message
  }
}

const clearErrors = (inputContainers: NodeListOf<Element>): void => {
  inputContainers.forEach((inputContainer) => setError(inputContainer, ''))
}

// Generate schema from HTML
const generateSchemaFromHTML = (inputs: NodeListOf<Element>): z.ZodObject => {
  let schema: z.ZodObject = z.object({})

  inputs.forEach((input) => {
    const inputType = input.getAttribute(globalConfig.inputSelector)
    const uniqueId = input.getAttribute(globalConfig.inputIdSelector)

    if (!inputType || !uniqueId) return

    const inputLabel =
      input.getAttribute(globalConfig.inputLabelSelector) ?? inputType

    switch (inputType) {
      case FormInputType.INPUT_TEXT:
        schema = schema.extend({
          [uniqueId]: getZodTypes({ minLength: 2 }).INPUT_TEXT(inputLabel),
        })
        break
      case FormInputType.TEXTAREA:
        schema = schema.extend({
          [uniqueId]: getZodTypes({ minLength: 10 }).TEXTAREA(inputLabel),
        })
        break
      case FormInputType.EMAIL:
        schema = schema.extend({
          [uniqueId]: getZodTypes().EMAIL(inputLabel),
        })
        break
      case FormInputType.SELECT:
        schema = schema.extend({
          [uniqueId]: getZodTypes().SELECT(inputLabel),
        })
        break
      case FormInputType.CHECKBOX:
        schema = schema.extend({
          [uniqueId]: getZodTypes().CHECKBOX(),
        })
        break
      case FormInputType.RADIO:
        schema = schema.extend({
          [uniqueId]: getZodTypes().RADIO(inputLabel),
        })
        break
      case FormInputType.UPLOAD:
        schema = schema.extend({
          [uniqueId]: getZodTypes().UPLOAD('20MB', 'jpg/jpeg, png, pdf'),
        })
        break
    }
  })

  return schema
}

// Helper functions

const isFormElement = (form: Element): form is HTMLFormElement => {
  return form instanceof HTMLFormElement
}

const transformInput = (input: HTMLInputElement): void => {
  input.removeAttribute('required')

  if (input.type === 'email') input.type = 'text'
}

// Initialize form

const initializeForm = (form: Element): void => {
  if (!isFormElement(form)) return

  // Disable browser's native form validation
  form.setAttribute('novalidate', '')

  const formName = form.getAttribute(globalConfig.formSelector)
  const inputs = form.querySelectorAll(`[${globalConfig.inputSelector}]`)

  if (!formName || inputs.length === 0) return

  if (!schemas?.[formName]) {
    schemas[formName] = generateSchemaFromHTML(inputs)
  }

  handleSubmit(form)
  handleChange(form)
}

const processForms = (): void => {
  const forms = document.querySelectorAll(`[${globalConfig.formSelector}]`)

  if (forms.length === 0) return

  forms.forEach(initializeForm)
}

window.addEventListener('load', processForms)

// Mutation observer for dynamic forms
const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (!(node instanceof Element)) return

      if (node.matches(`[${globalConfig.formSelector}]`)) {
        initializeForm(node)
      }

      node
        .querySelectorAll(`[${globalConfig.formSelector}]`)
        .forEach(initializeForm)
    })
  })
})

if (globalConfig.observeForms) {
  observer.observe(document.body, { childList: true, subtree: true })
}
